import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useUpdateClienteIntermedioWithUsuario } from "@hooks/clientes-intermedios/useUpdateClienteIntermedioWithUsuario";
import type { ClienteIntermedio } from "@/interfaces/user.interface";

const schema = z.object({
  usuario: z.object({
    nombreUsuario: z.string().min(2),
    nombre: z.string().min(1),
    apellidoPaterno: z.string().min(1),
    apellidoMaterno: z.string().optional(),
    correo: z.string().email(),
  }),
  cliente: z.object({
    nombreEmpresa: z.string().min(1),
  }),
});

type FormData = z.infer<typeof schema>;

export function EditClienteIntermedioForm({
  data,
  close,
}: {
  data: ClienteIntermedio;
  close: () => void;
}) {
  const cliente = data;
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      usuario: {
        nombreUsuario: cliente.usuario.nombreUsuario,
        nombre: cliente.usuario.nombre,
        apellidoPaterno: cliente.usuario.apellidoPaterno,
        apellidoMaterno: cliente.usuario.apellidoMaterno ?? "",
        correo: cliente.usuario.correo,
      },
      cliente: {
        nombreEmpresa: cliente.nombreEmpresa,
      },
    },
  });

  const { mutate, isPending } = useUpdateClienteIntermedioWithUsuario();

  const onSubmit = (data: FormData) => {
    mutate(
      {
        id: typeof cliente.id === "number" ? cliente.id : Number(cliente.id),
        data,
      },
      {
        onSuccess: () => {
          close();
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 sm:space-y-6"
      >
        <FormField
          control={form.control}
          name="usuario.nombreUsuario"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Nombre de usuario</FormLabel>
              <FormControl>
                <Input placeholder="Nombre de usuario" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="usuario.nombre"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Nombre</FormLabel>
              <FormControl>
                <Input placeholder="Nombre" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="usuario.apellidoPaterno"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Apellido paterno</FormLabel>
              <FormControl>
                <Input placeholder="Apellido paterno" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="usuario.apellidoMaterno"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Apellido materno</FormLabel>
              <FormControl>
                <Input placeholder="Apellido materno" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="usuario.correo"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Correo</FormLabel>
              <FormControl>
                <Input placeholder="correo@ejemplo.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="cliente.nombreEmpresa"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Empresa</FormLabel>
              <FormControl>
                <Input placeholder="Empresa" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={isPending}
          className="w-full h-10 sm:h-11 text-sm sm:text-base"
        >
          {isPending ? "Guardando..." : "Guardar cambios"}
        </Button>
      </form>
    </Form>
  );
}
